/**
 * HERO-BG (#93) — recolour an uploaded line-art / monochrome SVG's markup so a
 * Template-Studio author's stroke colour and/or gradient fill VISIBLY apply.
 *
 * WHY a markup rewrite (not a CSS `mask-image`):
 *   The original recolour used `mask-image: url(<heroCover.imageUrl>)` over a
 *   gradient/stroke-coloured div. That has two problems:
 *     1. A CSS mask needs the resource to load as a mask, which fails CORS for a
 *        cross-origin SVG unless the host sets ACAO — Firebase Storage download
 *        URLs are not CORS-configured for mask resources by default.
 *     2. A mask only paints where the SVG is NON-transparent. For a line-art
 *        diagram the lines are thin, so the gradient/stroke is confined to the
 *        line pixels — which is what we want; but if the SVG has an OPAQUE
 *        background rectangle the mask paints a solid gradient BOX, hiding the
 *        geometry entirely.
 *
 *   Fetching the SVG text and recolouring its markup directly:
 *     - is origin-agnostic for the *result* (we inline same-origin markup), and
 *       the fetch itself is same-origin because the upload pipeline serves the
 *       hero via the token-gated `/api/storage/logo-file` proxy;
 *     - recolours the actual `stroke`/`fill` so transparent-background line art
 *       takes the new colour with NO box artefact;
 *     - supports a real `<linearGradient>` fill for the gradient case.
 *
 * This module is the PURE string transform (no DOM, no network) so it is unit-
 * testable in isolation; the React layer (`RecoloredHeroSvg`) does the fetch +
 * inline and falls back to a same-origin CSS mask while loading / on failure.
 */

export interface RecolorHeroSvgOptions {
  /** Stroke / line colour (hex). Applied to every stroked element. */
  strokeColor?: string;
  /**
   * Ordered gradient stops (≥2 hex). When supplied, fills take a top→bottom
   * `<linearGradient>`; the stroke (if any) is ALSO applied so line art reads.
   */
  gradientStops?: string[];
}

const GRADIENT_ID = 'da-hero-recolor-grad';

/** Escape a string for safe insertion into an XML attribute value. */
function escapeXmlAttr(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * Does this SVG markup carry any element that is actually STROKED? Used to
 * decide whether a pure-stroke recolour will produce visible line art. We treat
 * an explicit `stroke="…"` (not `none`) or a `stroke:` style as "stroked".
 */
export function svgMarkupHasStroke(svg: string): boolean {
  return /\bstroke\s*=\s*"(?!none"|"|transparent")/i.test(svg)
    || /\bstroke\s*:\s*(?!none|transparent|;)/i.test(svg);
}

/**
 * Recolour the SVG markup so the supplied stroke / gradient fill apply.
 *
 * Strategy (no DOM — operates on the serialized markup):
 *   - GRADIENT: inject a top→bottom `<linearGradient>` into a `<defs>` and force
 *     every existing `fill` (and the root default fill) to reference it, EXCEPT
 *     `fill="none"` which is preserved (that marks stroke-only line art). The
 *     gradient is given `objectBoundingBox` units so it spans the whole graphic.
 *   - STROKE: rewrite every existing `stroke="…"` (other than `none`) to the new
 *     colour and set a root-level `stroke` default so elements that inherit the
 *     stroke also recolour. Existing `stroke="none"` is preserved.
 *
 * Returns the original markup unchanged when neither a stroke nor a valid
 * (≥2-stop) gradient is supplied, or when the input is not an `<svg>`.
 */
export function recolorHeroSvgMarkup(svg: string, opts: RecolorHeroSvgOptions): string {
  const stroke = opts.strokeColor?.trim() || '';
  const stops = (opts.gradientStops ?? []).map(s => s?.trim() || '').filter(Boolean);
  const useGradient = stops.length >= 2;
  if (!svg || (!stroke && !useGradient)) return svg;

  // Locate the opening <svg …> tag. Bail out if this is not an SVG document.
  const openTagMatch = /<svg\b[^>]*>/i.exec(svg);
  if (!openTagMatch) return svg;
  const openTag = openTagMatch[0];
  const openTagEnd = openTagMatch.index + openTag.length;

  let body = svg.slice(openTagEnd);

  // --- GRADIENT FILL -------------------------------------------------------
  let gradientDefs = '';
  if (useGradient) {
    const last = stops.length - 1;
    const stopEls = stops
      .map((c, i) => `<stop offset="${Math.round((i / last) * 100)}%" stop-color="${escapeXmlAttr(c)}"/>`)
      .join('');
    gradientDefs =
      `<defs><linearGradient id="${GRADIENT_ID}" x1="0" y1="0" x2="0" y2="1" `
      + `gradientUnits="objectBoundingBox">${stopEls}</linearGradient></defs>`;
    // Repoint every existing fill EXCEPT `none` (stroke-only line art) and the
    // gradient's own stop-colors (handled above, not present in `body` yet).
    body = body.replace(/\bfill\s*=\s*"([^"]*)"/gi, (whole, val: string) =>
      /^\s*none\s*$/i.test(val) ? whole : `fill="url(#${GRADIENT_ID})"`,
    );
    body = body.replace(/\bfill\s*:\s*([^;"']+)/gi, (whole, val: string) =>
      /^\s*none\s*$/i.test(val) ? whole : `fill:url(#${GRADIENT_ID})`,
    );
  }

  // --- STROKE COLOUR -------------------------------------------------------
  if (stroke) {
    const safeStroke = escapeXmlAttr(stroke);
    body = body.replace(/\bstroke\s*=\s*"([^"]*)"/gi, (whole, val: string) =>
      /^\s*none\s*$/i.test(val) ? whole : `stroke="${safeStroke}"`,
    );
    body = body.replace(/\bstroke\s*:\s*([^;"']+)/gi, (whole, val: string) =>
      /^\s*none\s*$/i.test(val) ? whole : `stroke:${safeStroke}`,
    );
  }

  // --- ROOT DEFAULTS (so inherited fill/stroke also recolour) --------------
  // Strip any existing root-level fill/stroke attributes, then re-add ours so
  // inheritance picks up the recolour for elements with no explicit fill/stroke.
  let newOpenTag = openTag
    .replace(/\sfill\s*=\s*"[^"]*"/i, '')
    .replace(/\sstroke\s*=\s*"[^"]*"/i, '');
  const rootDefaults: string[] = [];
  if (useGradient) rootDefaults.push(`fill="url(#${GRADIENT_ID})"`);
  if (stroke) rootDefaults.push(`stroke="${escapeXmlAttr(stroke)}"`);
  newOpenTag = newOpenTag.replace(/>$/, ` ${rootDefaults.join(' ')}>`);

  return `${newOpenTag}${gradientDefs}${body}`;
}
